import { useEffect, useState } from "react";
import { CommandBar } from "./CommandBar";
import { LayerToggleHud } from "./LayerToggleHud";
import { AlertFeed } from "./AlertFeed";
import { GdprFooter } from "./GdprFooter";
import { MapStage } from "./MapStage";
import { ReportsArchive } from "./ReportsArchive";
import { DanaSimulation } from "./DanaSimulation";
import { getAlerts, getFlood, getInfra, getGpmConfig, getSaih, getEms, getRadarStatus } from "../lib/api";
import { LangContext } from "../lib/i18n";

const LAYERS = ["flood", "roads", "bridges", "ports", "airstrips"];

export default function Dashboard() {
  const [lang, setLangState] = useState(() => localStorage.getItem("resilio_lang") || "es");
  const setLang = (l) => {
    localStorage.setItem("resilio_lang", l);
    setLangState(l);
  };
  const [geo, setGeo] = useState({});
  const [sources, setSources] = useState({ gpm: "live", radar: "live", sentinel: "live", saih: "live", ems: "live" });
  const [counts, setCounts] = useState({ gpm: "IMERG", radar: "AEMET", sentinel: "OLCI", saih: "SAIH", ems: "EMS" });
  const [gpmConfig, setGpmConfig] = useState(null);
  const [visibility, setVisibility] = useState({
    gpm: true, radar: false, sentinel: false, saih: true, ems: false,
    flood: true, roads: true, bridges: true, ports: true, airstrips: true,
  });
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState({});
  const [focus, setFocus] = useState(null);
  const [view, setView] = useState("map");
  const [danaMode, setDanaMode] = useState(false);
  const [radarStatus, setRadarStatus] = useState(null);
  const [floodNotice, setFloodNotice] = useState(null);

  useEffect(() => {
    const loadAlerts = () =>
      getAlerts()
        .then((d) => {
          const fc = d.forecast || [];
          setAlerts([...fc, ...(d.alerts || [])]);
          setSummary({ ...(d.summary || {}), forecast: fc.length });
        })
        .catch(() => {});
    const loadRadar = () => getRadarStatus().then(setRadarStatus).catch(() => {});
    const loadLayers = () => {
      const loaders = {
        flood: getFlood(),
        roads: getInfra("roads"),
        bridges: getInfra("bridges"),
        ports: getInfra("ports"),
        airstrips: getInfra("airstrips"),
      };
      LAYERS.forEach((k) => {
        loaders[k]
          .then((fc) => {
            setGeo((g) => ({ ...g, [k]: fc }));
            setSources((s) => ({ ...s, [k]: fc.metadata?.source }));
            setCounts((c) => ({ ...c, [k]: fc.metadata?.feature_count ?? (fc.features || []).length }));
            if (k === "flood" && fc.metadata?.source !== "live") {
              setFloodNotice(fc.metadata?.notice || null);
            }
          })
          .catch(() => {});
      });
      getSaih().then((fc) => setGeo((g) => ({ ...g, saih: fc }))).catch(() => {});
      getEms().then((fc) => setGeo((g) => ({ ...g, ems: fc }))).catch(() => {});
    };

    loadAlerts();
    loadRadar();
    getGpmConfig().then(setGpmConfig).catch(() => {});
    loadLayers();
    // auto-refresh every 2 min — re-fetches layers too, so a 'sample' layer
    // recovers to 'live' on its own (backend retries IGN on fallback).
    const poll = setInterval(() => { loadAlerts(); loadRadar(); loadLayers(); }, 120000);
    return () => clearInterval(poll);
  }, []);

  const toggle = (k) => setVisibility((v) => ({ ...v, [k]: !v[k] }));

  return (
    <LangContext.Provider value={lang}>
      <div className="h-screen w-screen flex flex-col bg-[#06090E] overflow-hidden">
        <CommandBar summary={summary} setLang={setLang} view={view} setView={setView} danaMode={danaMode} setDanaMode={setDanaMode} radarStatus={radarStatus} />
        <div className="flex-1 flex min-h-0">
          {danaMode ? (
            <DanaSimulation />
          ) : view === "map" ? (
            <>
              <MapStage geo={geo} visibility={visibility} focus={focus} gpmConfig={gpmConfig} alerts={alerts}>
                <LayerToggleHud visibility={visibility} toggle={toggle} counts={counts} sources={sources} radarStatus={radarStatus} floodNotice={floodNotice} />
              </MapStage>
              <AlertFeed alerts={alerts} summary={summary} onFocus={setFocus} />
            </>
          ) : (
            <ReportsArchive />
          )}
        </div>
        <GdprFooter />
      </div>
    </LangContext.Provider>
  );
}
